(() => {
  const RFQ_KEY = 'airzoe-rfq-list-v1';
  const PORTS = ['1/4" NPT', '3/8" NPT', '1/2" NPT', '3/4" NPT', '1" NPT'];
  const DRAINS = ['Manual Drain', 'Semi-Auto Drain', 'Auto Drain'];

  const clean = (value) => String(value || '')
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();

  const attributes = (title) => {
    const text = clean(title);
    let family = '';
    if (text.includes('Air Dryer Water Separator Combo')) family = 'dryer-combo';
    else if (text.includes('Air Dryer Water Separator')) family = 'dryer';
    else if (text.includes('Air Filter Regulator Lubricator Combo')) family = 'frl-combo';
    else if (text.includes('FRL Air Filter Regulator Lubricator')) family = 'frl';
    else if (text.includes('Piggyback Air Filter Regulator')) family = 'piggyback';
    else if (text.includes('Air Pressure Regulator')) family = 'regulator';

    const match = text.match(/(?:^|\s)(1\/4|3\/8|1\/2|3\/4|1)\s*(?:"|inch)?\s*NPT/i);
    let drain = '';
    if (/Manual Drain/i.test(text)) drain = 'Manual Drain';
    else if (/Semi[- ]Auto Drain/i.test(text)) drain = 'Semi-Auto Drain';
    else if (/Auto Drain/i.test(text)) drain = 'Auto Drain';

    return {
      family,
      port: match ? `${match[1]}" NPT` : '',
      drain,
      ballValve: /Ball Valve/i.test(text)
    };
  };

  const copy = async (text) => {
    if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
    const field = document.createElement('textarea');
    field.value = text;
    field.readOnly = true;
    field.style.cssText = 'position:fixed;opacity:0';
    document.body.appendChild(field);
    field.select();
    document.execCommand('copy');
    field.remove();
  };

  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  class ModelSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      const source = this.querySelector('[data-airzoe-products]');
      const output = this.querySelector('[data-airzoe-selector-output]');
      if (!source || !output) return;
      try {
        this.products = JSON.parse(source.textContent).map((product) => ({
          ...product,
          attributes: {
            port: clean(product.port),
            drain: clean(product.drain)
          }
        }));
      } catch (_) {
        return;
      }
      this.current = this.products.find((item) => String(item.id) === this.dataset.productId) || {
        id: this.dataset.productId,
        title: this.dataset.productTitle,
        url: this.dataset.productUrl,
        attributes: {
          port: clean(this.dataset.portSize),
          drain: clean(this.dataset.drainType)
        }
      };
      this.render(output);
      this.addEventListener('pointerenter', (event) => this.prefetch(event), true);
      this.addEventListener('focusin', (event) => this.prefetch(event), true);
      window.addEventListener('pagehide', () => this.restorePreview(), { once: true });
    }

    related() {
      return this.products;
    }

    best(items, preferredDrain) {
      return items.find((item) => item.attributes.drain === preferredDrain)
        || DRAINS.map((drain) => items.find((item) => item.attributes.drain === drain)).find(Boolean)
        || items[0];
    }

    group(title, type, items) {
      if (!items.length) return null;
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'airzoe-option-group';
      fieldset.dataset.airzoeOptionGroup = type;
      const legend = document.createElement('legend');
      legend.textContent = title;
      const list = document.createElement('div');
      list.className = 'airzoe-option-list';
      items.forEach(({ product, label }) => {
        const link = document.createElement('a');
        link.className = 'airzoe-option';
        link.href = product.url;
        link.dataset.airzoePrefetch = product.url;
        link.dataset.airzoeOptionValue = label;
        if (product.image) link.dataset.airzoePreview = product.image;
        if (String(product.id) === String(this.current.id)) {
          link.classList.add('is-active');
          link.setAttribute('aria-current', 'page');
        }
        if (!product.available) link.classList.add('is-unavailable');
        const name = document.createElement('span');
        name.textContent = label.replace(' Drain', '');
        const price = document.createElement('small');
        price.textContent = product.available ? product.price : 'Sold out';
        link.append(name, price);
        if (product.image) {
          link.addEventListener('mouseenter', () => this.preview(link));
          link.addEventListener('mouseleave', () => this.restorePreview());
          link.addEventListener('focus', () => this.preview(link));
          link.addEventListener('blur', () => this.restorePreview());
        }
        list.appendChild(link);
      });
      fieldset.append(legend, list);
      return fieldset;
    }

    render(output) {
      const related = this.related();
      if (!related.length) return;
      const current = this.current.attributes;
      const portItems = PORTS.flatMap((port) => {
        const matches = related.filter((item) => item.attributes.port === port);
        if (!matches.length) return [];
        const hasDrainOptions = matches.some((item) => item.attributes.drain);
        if (matches.length > 1 && !hasDrainOptions) {
          return matches.map((product) => ({
            product,
            label: `${port} — ${product.sku || 'Model'}`
          }));
        }
        return [{ product: this.best(matches, current.drain), label: port }];
      });
      const samePort = related.filter((item) => item.attributes.port === current.port);
      const drainItems = DRAINS.map((drain) => {
        const product = samePort.find((item) => item.attributes.drain === drain);
        return product ? { product, label: drain } : null;
      }).filter(Boolean);

      const portGroup = this.group('Port Size', 'Port Size', portItems);
      const drainGroup = drainItems.length > 1 ? this.group('Drain Type', 'Drain Type', drainItems) : null;
      if (portGroup) output.appendChild(portGroup);
      if (drainGroup) output.appendChild(drainGroup);
      this.dispatchEvent(new CustomEvent('airzoe:selector-ready', { bubbles: true }));
    }

    prefetch(event) {
      const link = event.target.closest('[data-airzoe-prefetch]');
      if (!link || link.dataset.prefetched) return;
      link.dataset.prefetched = 'true';
      const tag = document.createElement('link');
      tag.rel = 'prefetch';
      tag.href = link.dataset.airzoePrefetch;
      document.head.appendChild(tag);
    }

    findMainImage() {
      const gallery = document.querySelector('media-gallery');
      if (!gallery) return null;
      return gallery.querySelector('[data-media-id].active .product-media__image')
        || gallery.querySelector('.swiper-slide-active .product-media__image')
        || gallery.querySelector('.product-media__image');
    }

    preview(link) {
      const imageUrl = link.dataset.airzoePreview;
      if (!imageUrl) return;
      this.restorePreview();
      const image = this.findMainImage();
      if (!image) return;
      this.previewImage = image;
      this.originalImage = {
        src: image.getAttribute('src'),
        srcset: image.getAttribute('srcset'),
        sizes: image.getAttribute('sizes')
      };
      const preload = new Image();
      preload.src = imageUrl;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = imageUrl;
      image.classList.add('airzoe-previewing');
    }

    restorePreview() {
      if (!this.previewImage || !this.originalImage) return;
      ['src', 'srcset', 'sizes'].forEach((name) => {
        const value = this.originalImage[name];
        if (value === null) this.previewImage.removeAttribute(name);
        else this.previewImage.setAttribute(name, value);
      });
      this.previewImage.classList.remove('airzoe-previewing');
      this.previewImage = null;
      this.originalImage = null;
    }
  }

  class ProductTools extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.status = this.querySelector('[data-airzoe-status]');
      this.quantityInput = document.querySelector('.product-form__quantity input[name="quantity"], input[name="quantity"]');
      this.addEventListener('click', (event) => this.action(event));
      this.addEventListener('input', (event) => this.input(event));
      this.addEventListener('change', (event) => this.input(event));
      document.addEventListener('airzoe:selector-ready', () => this.summary());
      this.quantityInput?.addEventListener('input', () => this.summary());
      this.quantityInput?.addEventListener('change', () => this.summary());
      this.summary();
    }

    get quantity() {
      return Math.max(1, Number(this.quantityInput?.value || 1));
    }

    get configuration() {
      const values = Array.from(document.querySelectorAll(
        '[data-airzoe-option-group] .airzoe-option.is-active'
      )).map((option) => {
        const group = option.closest('[data-airzoe-option-group]')?.dataset.airzoeOptionGroup;
        return group ? `${group}: ${option.dataset.airzoeOptionValue}` : '';
      }).filter(Boolean);
      return values.length ? values.join('; ') : (this.dataset.portSize ? `Port Size: ${this.dataset.portSize}` : 'As shown');
    }

    summary() {
      const config = this.querySelector('[data-airzoe-configuration]');
      const quantity = this.querySelector('[data-airzoe-quantity]');
      if (config) config.textContent = this.configuration;
      if (quantity) quantity.textContent = String(this.quantity);
      this.syncFields();
    }

    message(text, error = false) {
      if (!this.status) return;
      this.status.textContent = text;
      this.status.style.color = error ? '#a32929' : '#126b51';
      clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => { this.status.textContent = ''; }, 4500);
    }

    detailsText() {
      return [
        `Product: ${this.dataset.productTitle}`,
        `SKU: ${this.dataset.sku || 'Not assigned'}`,
        `Configuration: ${this.configuration}`,
        `Quantity: ${this.quantity}`,
        `URL: ${this.dataset.productUrl}`
      ].join('\n');
    }

    async action(event) {
      const button = event.target.closest('[data-airzoe-action]');
      if (!button) return;
      try {
        switch (button.dataset.airzoeAction) {
          case 'copy-sku':
            await copy(this.dataset.sku || 'Not assigned');
            this.message('SKU copied.');
            break;
          case 'copy-configuration':
            await copy(this.detailsText());
            this.message('Configuration copied.');
            break;
          case 'share':
            await this.share();
            break;
          case 'check-fit':
            this.checkFit();
            break;
          case 'ask-specialist':
            this.openInquiry();
            break;
          case 'add-rfq':
            this.addRfq();
            break;
          case 'copy-rfq':
            await copy(this.rfqText());
            this.message('RFQ list copied.');
            break;
          case 'ask-rfq':
            this.syncRfq();
            this.openInquiry();
            break;
          case 'clear-rfq':
            localStorage.removeItem(RFQ_KEY);
            this.renderRfq();
            this.syncRfq();
            this.message('RFQ list cleared.');
            break;
        }
      } catch (_) {
        this.message('This action could not be completed. Please try again.', true);
      }
    }

    input(event) {
      if (event.target.matches('[data-convert-pressure], [data-convert-pressure-unit]')) this.convertPressure();
      if (event.target.matches('[data-convert-flow], [data-convert-flow-unit]')) this.convertFlow();
    }

    async share() {
      const data = { title: this.dataset.productTitle, text: this.detailsText(), url: this.dataset.productUrl };
      if (navigator.share) {
        await navigator.share(data);
        this.message('Configuration shared.');
      } else {
        await copy(`${data.text}\n${data.url}`);
        this.message('Share details copied.');
      }
    }

    convertPressure() {
      const value = Number(this.querySelector('[data-convert-pressure]')?.value);
      const unit = this.querySelector('[data-convert-pressure-unit]')?.value;
      const output = this.querySelector('[data-pressure-output]');
      if (!Number.isFinite(value)) {
        if (output) output.textContent = 'Enter a value to convert.';
        return;
      }
      const psi = unit === 'bar' ? value * 14.5038 : unit === 'mpa' ? value * 145.038 : value;
      output.textContent = `${psi.toFixed(2)} PSI | ${(psi / 14.5038).toFixed(3)} bar | ${(psi / 145.038).toFixed(4)} MPa`;
    }

    convertFlow() {
      const value = Number(this.querySelector('[data-convert-flow]')?.value);
      const unit = this.querySelector('[data-convert-flow-unit]')?.value;
      const output = this.querySelector('[data-flow-output]');
      if (!Number.isFinite(value)) {
        if (output) output.textContent = 'Enter a value to convert.';
        return;
      }
      const litres = unit === 'cfm' ? value * 28.3168 : value;
      output.textContent = `${litres.toFixed(1)} L/min | ${(litres / 28.3168).toFixed(2)} CFM`;
    }

    spec(labels) {
      const rows = document.querySelectorAll('.airzoe-description-table tr, .airzoe-spec-grid > div');
      for (const row of rows) {
        const text = clean(row.textContent);
        if (labels.some((label) => text.toLowerCase().includes(label))) return text;
      }
      return '';
    }

    maxNumber(text) {
      const numbers = (text.replace(/,/g, '').match(/\d+(?:\.\d+)?/g) || []).map(Number);
      return numbers.length ? Math.max(...numbers) : null;
    }

    checkFit() {
      const notes = [];
      const thread = this.querySelector('[data-fit-thread]')?.value;
      const port = this.querySelector('[data-fit-port]')?.value;
      const pressure = Number(this.querySelector('[data-fit-pressure]')?.value || 0);
      const flow = Number(this.querySelector('[data-fit-flow]')?.value || 0);
      const productPort = this.dataset.portSize || attributes(this.dataset.productTitle).port;
      if (thread === 'BSP') notes.push('Thread mismatch: this listing uses NPT, not BSP/BSPT/BSPP.');
      if (thread === 'unknown') notes.push('Confirm the thread standard before ordering.');
      if (port && productPort && port !== productPort) notes.push(`Port mismatch: selected ${productPort}; required ${port}.`);

      const pressureText = this.spec(['working pressure', 'pressure range']);
      const maxPressure = this.maxNumber(pressureText);
      if (pressure > 0 && maxPressure && pressure > maxPressure) {
        notes.push(`Required pressure exceeds the listed maximum of approximately ${maxPressure} PSI.`);
      } else if (pressure > 0 && !pressureText) {
        notes.push('Working pressure could not be verified from the page specifications.');
      }

      const flowText = this.spec(['rated airflow', 'air flow', 'flow rate']);
      const maxFlow = this.maxNumber(flowText);
      if (flow > 0 && maxFlow && flow > maxFlow) {
        notes.push(`Required airflow exceeds the listed value of approximately ${maxFlow} L/min.`);
      } else if (flow > 0 && !flowText) {
        notes.push('Airflow could not be verified from the page specifications.');
      }

      const result = this.querySelector('[data-airzoe-fit-result]');
      if (!result) return;
      result.replaceChildren();
      result.hidden = false;
      const heading = document.createElement('strong');
      heading.textContent = notes.length ? 'Review required' : 'Selection looks consistent';
      result.appendChild(heading);
      if (notes.length) {
        const list = document.createElement('ul');
        notes.forEach((note) => {
          const item = document.createElement('li');
          item.textContent = note;
          list.appendChild(item);
        });
        result.appendChild(list);
      } else {
        const note = document.createElement('p');
        note.textContent = 'The entered values are consistent with the information detected on this page. Confirm the datasheet before installation.';
        result.appendChild(note);
      }
    }

    readRfq() {
      try {
        const items = JSON.parse(localStorage.getItem(RFQ_KEY) || '[]');
        return Array.isArray(items) ? items : [];
      } catch (_) {
        return [];
      }
    }

    addRfq() {
      const items = this.readRfq();
      const item = {
        product: this.dataset.productTitle,
        sku: this.dataset.sku || '',
        configuration: this.configuration,
        quantity: this.quantity,
        variantId: this.dataset.variantId,
        url: this.dataset.productUrl
      };
      const index = items.findIndex((entry) => entry.variantId === item.variantId && entry.configuration === item.configuration);
      if (index >= 0) items[index] = item;
      else items.push(item);
      localStorage.setItem(RFQ_KEY, JSON.stringify(items.slice(-25)));
      this.renderRfq();
      this.syncRfq();
      this.message('Added to the local RFQ list.');
    }

    renderRfq() {
      const items = this.readRfq();
      const count = this.querySelector('[data-rfq-count]');
      const list = this.querySelector('[data-airzoe-rfq-list]');
      if (count) count.textContent = `(${items.length})`;
      if (!list) return;
      list.replaceChildren();
      if (!items.length) {
        const empty = document.createElement('p');
        empty.className = 'airzoe-rfq-empty';
        empty.textContent = 'No products added yet.';
        list.appendChild(empty);
        return;
      }
      items.forEach((item) => {
        const row = document.createElement('div');
        row.className = 'airzoe-rfq-item';
        const info = document.createElement('div');
        const title = document.createElement('strong');
        title.textContent = item.product;
        const meta = document.createElement('span');
        meta.textContent = `${item.sku || 'No SKU'} | ${item.configuration} | Qty ${item.quantity}`;
        const link = document.createElement('a');
        link.href = item.url;
        link.textContent = 'View';
        info.append(title, meta);
        row.append(info, link);
        list.appendChild(row);
      });
    }

    rfqText() {
      const items = this.readRfq();
      if (!items.length) return 'RFQ list is empty.';
      return items.map((item, index) => [
        `${index + 1}. ${item.product}`,
        `SKU: ${item.sku || 'Not assigned'}`,
        `Configuration: ${item.configuration}`,
        `Quantity: ${item.quantity}`,
        `URL: ${item.url}`
      ].join('\n')).join('\n\n');
    }

    syncFields() {
      const values = {
        product: this.dataset.productTitle,
        sku: this.dataset.sku || '',
        configuration: this.configuration,
        quantity: String(this.quantity),
        'variant-id': this.dataset.variantId,
        'product-url': this.dataset.productUrl
      };
      document.querySelectorAll('[data-contact-form-type="ask-an-expert"]').forEach((wrapper) => {
        Object.entries(values).forEach(([key, value]) => {
          const field = wrapper.querySelector(`[data-inquiry-field="${key}"]`);
          const preserveQuantity = key === 'quantity' && field?.dataset.userEdited === 'true';
          if (field && !preserveQuantity) field.value = value;

          const display = wrapper.querySelector(`[data-inquiry-display="${key}"]`);
          if (display) display.textContent = preserveQuantity ? field.value : (value || 'As shown');
        });
      });
    }

    syncRfq() {
      document.querySelectorAll('[data-contact-form-type="ask-an-expert"] form').forEach((form) => {
        let field = form.querySelector('[data-inquiry-field="rfq-list"]');
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'contact[RFQ List]';
          field.dataset.inquiryField = 'rfq-list';
          form.appendChild(field);
        }
        field.value = this.rfqText();
      });
    }

    openInquiry() {
      this.syncFields();
      const button = document.querySelector('.ask-an-expert .popup__button');
      if (button) button.click();
      else this.message('The inquiry form is not available on this template.', true);
    }
  }

  class CompatibleProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.boxes = Array.from(this.querySelectorAll('[data-compatible-variant]'));
      this.button = this.querySelector('[data-compatible-add]');
      this.status = this.querySelector('[data-compatible-status]');
      this.boxes.forEach((box) => box.addEventListener('change', () => this.update()));
      this.button?.addEventListener('click', () => this.add());
      this.update();
    }

    update() {
      const selected = this.boxes.filter((box) => box.checked);
      const total = selected.reduce((sum, box) => sum + Number(box.dataset.price || 0), Number(this.dataset.mainPrice || 0));
      const output = this.querySelector('[data-compatible-total]');
      if (output) output.textContent = formatMoney(total);
      if (this.button) this.button.disabled = !selected.length;
    }

    async add() {
      const selected = this.boxes.filter((box) => box.checked);
      if (!selected.length || !this.button) return;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      try {
        const items = [
          { id: Number(this.dataset.mainVariantId), quantity: 1 },
          ...selected.map((box) => ({ id: Number(box.value), quantity: 1 }))
        ];
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Cart request failed');
        window.location.href = `${root}cart`;
      } catch (_) {
        if (this.status) this.status.textContent = 'The selected setup could not be added. Please add each item separately.';
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('airzoe-model-selector')) customElements.define('airzoe-model-selector', ModelSelector);
  if (!customElements.get('airzoe-product-tools')) customElements.define('airzoe-product-tools', ProductTools);
  if (!customElements.get('airzoe-compatible-products')) customElements.define('airzoe-compatible-products', CompatibleProducts);
})();
